import { html, LitElement, css, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

import '../components/comments';
import '../parts/card';
import '../parts/monster';
import '../parts/affix';

import style from './challenge.scss';
import { formatTimespan, friendlyURL, getNameFromURL, isTouchDevice, setTitle, timespanPercentage, trackPage } from '../shared/utils';
import { DATA_AREAS, DATA_CLASSES } from '../data/sources';
import {DeckListComponent} from '../components/decklist';

export class ChallengePage extends LitElement {

    static get properties() {
        return {            
            challenge: Object,
            diff: String
        }
    }

    constructor() {
        super();    
        this.uid = null;
        this.challenge = null;
        this.diff = '';
        this.decklist = new DeckListComponent();
        this.decklist.title = ' ';
        this.decklist.showpoints = true;
    }

    static styles = css`
        ${unsafeCSS(style)}        
    `;

    connectedCallback() {
        super.connectedCallback();
        trackPage();      
        this.uid = getNameFromURL();                    
        setTitle('Challenge');
        this.fetchData();
    }

    async fetchData() {                                
        let ret = await fetch(`./api/challenge/${this.uid}`);
        let json = await ret.json();     
        this.challenge = json.challenge || json;                      
        if(json.decks){
            this.challenge.decks = json.decks;
        }         
        setTitle(this.challenge.name);
        this.decklist.url = `challengedecks/${this.challenge?.uid}`;
        this.update();       
    }

    classArtwork(name){
        return DATA_CLASSES.filter(c=>c.name===name)[0]?.artwork;
    }

    render() {   
        if(!this.challenge)
            return html`Loading...`;   

        let cls = null;
        if(this.challenge.class){
            cls = DATA_CLASSES.filter(c=>c.name===this.challenge.class)[0];
            if(this.challenge.class==='Scion'){
                cls = {
                    name: 'Sunforge Run',
                    artwork: 'cardart_5_50'
                };
            }
        }
        let classes = null;
        if(this.challenge.classes?.length > 0){
            classes = DATA_CLASSES.filter(c=>this.challenge.classes.indexOf(c.name) !== -1);
        }
        let ts = formatTimespan(new Date(),new Date(this.challenge.to),true) + ' left';      
        if(new Date(this.challenge.to) < new Date())     
            ts = "Finished";     
        if(new Date(this.challenge.from) > new Date())     
            ts = "Not Started";         
        let perc = timespanPercentage(this.challenge.from,this.challenge.to);
        if(perc > 100)
            perc = 100;

        const diffs = ['Impossible', 'Hard', 'Challenging', 'Normal'];

        const talents = this.challenge.additionalTalents?.filter(t=>t.indexOf('Offer of ') === -1);
        const offerings = this.challenge.additionalTalents?.filter(t=>t.indexOf('Offer of ') !== -1);

        return html`
            <h1>${this.challenge.image?html`<card-part id=${this.challenge.image} imageonly="true" header="true"></card-part>`:''}${this.challenge.name}</h1>
            ${this.challenge.intro?html`
                <p class="news-blue">${this.challenge.intro}</p>
            `:''}
            <p>
                Post runs between <strong>${(new Date(this.challenge.from)).toLocaleString()}</strong> to <strong>${(new Date(this.challenge.to)).toLocaleString()}</strong> (your local timezone).
            </p>
            <div class="content">
                <h2>Rules</h2>
                <ul>
                <li>Minimum game version ${this.challenge.version} required</li>
                ${cls?.name === 'Sunforge Run' ? html`
                <li>Enter the Trial of the Sunforge (the option at the far right when you are selecting a Class)</li>
                `:html`
                ${cls?html`
                <li>Create a new game and choose the Weapon, Power, Card and Card Packs provided</li>
                `:html`
                <li>Create a new game and choose one of the classes below</li>
                `}
                ${this.challenge.flags?.indexOf('randommaps')!==-1?html`
                <li>Please use the "Randomize Maps" option and choose your own Difficulty</li>
                `:html`
                <li>You may choose your own ${this.challenge.bosses?.length > 0 || this.challenge.maps?.length > 0 ? '' : 'Maps and '}Difficulty</li>
                `}     
                `}                
                ${this.challenge.affixes?.length > 0 ? html`
                <li>You must select the Malignant Affixes below (you can also add others if you'd like)</li>
                `:''}           
                <li>Post your completed game snippet in the <strong>🏅-weekly-challenges</strong> channel in discord</li>
                <li>Enter as many times as you like</li>
                <li>Have fun!</li>
                </ul>
                ${this.challenge.notes ? unsafeHTML(this.challenge.notes):''}
                ${classes ? html`
                <h2>Character</h2>                
                <p>You may choose between:</p>
                <div>
                    ${classes.map(cls=>html`
                    <div class="bubble big class-${(cls?.name||'scion').toLowerCase()}" style="line-height:30px;font-weight:bold;padding: 14px;display:inline-block;">
                        <img loading="lazy" class="class-image" src='./images/classes/${cls.artwork}.webp' alt=${cls.name} />                              
                        ${cls.name}
                    </div>
                    `)}                                          
                </div>
                <p>You may choose any combination of Weapon + Weapon Power + Starting Card</p>
                `:''} 
                ${cls ? html`
                <h2>Character</h2>
                ${this.challenge.level?html`<h3 style="color:#999">Requires Rank ${this.challenge.level}</h3>`:''}
                <div>
                    <div class="bubble big class-${(cls?.name||'scion').toLowerCase()}" style="line-height:30px;font-weight:bold;padding: 14px;display:inline-block;">
                        <img loading="lazy" class="class-image" src='./images/classes/${cls.artwork}.webp' alt=${cls.name} />                              
                        ${cls.name}
                    </div>     
                    ${this.challenge.weapon?html`<card-part id=${this.challenge.weapon} class="bubble big"></card-part>`:''}
                    ${this.challenge.power?html`<card-part id=${this.challenge.power} class="bubble big" talent="true"></card-part>`:''}
                    ${this.challenge.card?html`<card-part id=${this.challenge.card} class="bubble big"></card-part>`:''}                    
                </div>
                `:''} 
                ${this.challenge.setups?.length > 0 ? html`                            
                <p><b>Choose one of the following Setups:</b></p>
                <div>
                    ${this.challenge.setups.map((setup,i)=>html`
                        ${i>0?html`<br><br>`:''}
                        ${!cls?html`
                        <div class="bubble big class-${(setup.class||'scion').toLowerCase()}" style="line-height:30px;font-weight:bold;padding: 14px;display:inline-block;">
                            <img loading="lazy" class="class-image" src='./images/classes/${this.classArtwork(setup.class)}.webp' alt=${setup.class} />                              
                            ${setup.class}
                        </div>  
                        `:''} 
                        ${setup.weapon?html`<card-part id=${setup.weapon} class="bubble big"></card-part>`:''}
                        ${setup.power?html`<card-part id=${setup.power} class="bubble big" talent="true"></card-part>`:''}
                        ${setup.card?html`<card-part id=${setup.card} class="bubble big"></card-part>`:''}                                                    
                    `)}                                          
                </div>                
                `:''} 
                ${this.challenge.bosses?.length > 0 ?html`
                <h2>Bosses Required</h2>
                ${this.challenge.bosses.map(b=>html`
                <monster-part id=${b} class="bubble big"></card-monster>  
                `)}
                `:''}
                ${this.challenge.maps?.length > 0 ?html`
                <h2>Maps</h2>
                ${this.challenge.maps.map(b=>{
                    let areas = DATA_AREAS.filter(a=>a.name === b);
                    if(areas.length > 0){
                        const area = areas[0];
                        return html`
                        <div class="bubble big">
                            <a href="./area/${friendlyURL(area.name)}"><img loading="lazy" class="world" src="./images/card_world_${+area.name.substr(0, 2)}.webp" alt=${area.areaName} /> <div class="world-name">${area.areaName.replace('<br>', ' ')}</div></a>
                        </div>
                        `;
                    }
                })}
                `:''}
                ${talents?.length > 0 ?html`
                <h2>TALENTS</h2>
                ${talents.map(a=>html`
                    <div class="bubble big">
                        <card-part id=${a} talent="true"></card-part>
                    </div>
                `)}
                `:''}
                ${this.challenge.affixes?.length > 0 ?html`
                <h2>MALIGNANT AFFIXES</h2>
                ${this.challenge.affixes.map(a=>html`
                    <div class="bubble big">
                        <affix-part id=${a}></affix-part>
                    </div>
                `)}
                `:''}       
                ${offerings?.length > 0 ?html`
                    <h2>Infernal Offerings</h2>
                    ${offerings.map(a=>html`
                        <div class="bubble big">
                            <card-part id=${a} talent="true"></card-part>
                        </div>
                    `)}
                    `:''}
                ${cls?.name !== 'Sunforge Run' ? html`
                <h2>Card packs allowed</h2>
                ${this.challenge.expansions.map(e=>html`<span class="bubble big">${e}</span>`)} 
                ${this.challenge.expansions.length < 5 ? html`
                <h2>Not allowed</h2>
                ${['Core','Progression','Metamorphosis','Infinitum','Catalyst','Eclypse'].map(e=>{
                    if(this.challenge.expansions.indexOf(e)===-1) 
                        return html`<span class="bubble big" style="background:#390f0f;">${e}</span>`
                })} 
                `:''}
                `:''}                                
            </div>       
            ${this.challenge.winners?.length > 0 ? html`
                <h2>Winners</h2>                    
                ${this.challenge.winners.map((win,i) => html`
                    <div class="line"><b>${diffs[i]}</b>${isTouchDevice()?html`<br>`:''} <discorduser-component user=${win}></discorduser-component></div>
                `)}
            `: ''}     
            <span class="bubble big progress" style="margin-top: 20px"><span class="progress-bar" style="width:${perc}%;"></span><h3 class="progress-text">${this.challenge.decks||0} Runs submitted</h3><h3 class="progress-text-right">${ts}</h3></span>
            ${cls?.name !== 'Sunforge Run' ? html`
            <select @change=${(ev)=>{
                    this.diff = ev.target.value;                            
                    this.decklist.diff = this.diff;
                    this.decklist.fetchData();
                }}>
                <option value="">-- Filter Difficulty --</option>
                ${['Normal','Challenging','Hard','Impossible'].map((val,i)=>{
                    if(val)
                        return html`<option value="${val}" ?selected=${this.diff === val}>${val}</option>`;
                })}
            </select>  
            `:''}
            ${this.decklist}
            <comments-component path=${window.location.pathname}></comments-component>
        `;
    }
}

customElements.define('challenge-page', ChallengePage);